import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { formatEastern, formatDollars as dollars } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Who used a referral code and where the money stands (app/referrals.py studio_list). One list,
// two readers: the referrer on their Gem Bag card, where it arrives with the code (/referrals/mine),
// and the team on the admin drawer (/referrals/{client}/studios). Every figure comes from the
// server, so nobody adds anything up; an order that does not count under the referral rule never
// reaches this list at all. Read-only: no actions, no nudges.

const studios = (n) => `${n} ${n === 1 ? 'studio' : 'studios'}`;
const orders = (n) => `${n} paid ${n === 1 ? 'order' : 'orders'}`;
const ORDINAL = { 2: 'second', 3: 'third' };
// The client's own clock, like the loyalty credit's expiry on the same card; the team reads Eastern.
const clientDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const adminDate = (iso) => formatEastern(iso, { dateOnly: true });

// Exactly one line per studio. Anyone reads it and knows whether they are getting paid.
//   1 paid order so far. Their next paid order earns you $100.
//   2 paid orders so far. $100 earned on Sep 3, 2026 (used). Their next paid order earns you $50.
//   $150 earned. $100 on Sep 3, 2026 (used), $50 on Sep 20, 2026.
// A reward the order count has earned but that is not issued yet (`pending`, the server's word, retried
// daily) is on its way, and after PENDING_DELAYED_DAYS is delayed, with a way to write in.
function statusLine(row, amounts, admin) {
  const date = admin ? adminDate : clientDate;
  const each = row.earned.map(e => `${dollars(e.amount)} on ${date(e.on)}${e.used ? ' (used)' : ''}`);
  const total = dollars(row.earned.reduce((s, e) => s + e.amount, 0));
  const next = Object.keys(amounts).map(Number).sort().find(t => !row.earned.some(e => e.tier === t));
  if (row.blocked) {
    return each.length ? `${total} earned. ${each.join(', ')}. This one earns nothing more.` : 'This one does not earn a reward.';
  }
  if (next === undefined) return `${total} earned. ${each.join(', ')}.`;
  const parts = [row.paid_orders === 0 ? 'No paid orders yet.' : `${orders(row.paid_orders)} so far.`];
  row.earned.forEach(e => parts.push(`${dollars(e.amount)} earned on ${date(e.on)}${e.used ? ' (used)' : ''}.`));
  const reward = dollars(amounts[String(next)]);
  if (row.pending) {
    parts.push(row.pending.delayed
      ? `Your ${dollars(row.pending.amount)} reward is delayed. Email us at hello@uncutgems.com and we will sort it out.`
      : `Your ${dollars(row.pending.amount)} reward is on its way.`);
  }
  else if (row.paid_orders === next - 1) parts.push(`Their next paid order earns you ${reward}.`);
  else parts.push(`Their ${ORDINAL[next]} paid order earns you ${reward}.`);
  return parts.join(' ');
}

// The admin filters: where a studio stopped, or a reward the daily pass has not managed to send.
const FILTERS = [
  { key: 1, label: 'Stopped at one order', keep: r => r.stopped_at === 1, none: 'No studio stopped at one order.' },
  { key: 2, label: 'Stopped at two orders', keep: r => r.stopped_at === 2, none: 'No studio stopped at two orders.' },
  { key: 'pending', label: 'Reward not sent', keep: r => Boolean(r.pending), none: 'Every reward earned so far has been sent.' },
];

export function ReferralList({ data, admin = false }) {
  const [filterKey, setFilterKey] = useState(null);
  if (data.list_unavailable) {
    return <p data-testid="referral-list-unavailable" className="font-sans text-xs text-zinc-400">{data.list_unavailable}</p>;
  }
  if (data.used === 0) {
    return (
      <p data-testid="referral-list-empty" className="font-sans text-xs text-zinc-100/70">
        Nobody has used your code yet. Give a friend {data.friend_percent}% off their first order. You get {dollars(data.reward_amounts['2'])} when they place their second order and {dollars(data.reward_amounts['3'])} more on their third. Who used your code and what you have earned will show up here once someone uses it.
      </p>
    );
  }
  const filter = FILTERS.find(f => f.key === filterKey);
  const rows = filter ? data.studios.filter(filter.keep) : data.studios;
  const filterPill = (f) => (
    <button key={f.key} type="button" onClick={() => setFilterKey(v => (v === f.key ? null : f.key))}
      data-testid={`referral-list-filter-${f.key}`} aria-pressed={filterKey === f.key}
      className={`tap-auto shrink-0 font-sans text-[9px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${
        filterKey === f.key ? 'text-oxidized-teal border-oxidized-teal/40 bg-oxidized-teal/[0.07]' : 'text-slate-400 border-ug-border'}`}>
      {f.label}
    </button>
  );
  return (
    <div data-testid="referral-list">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p data-testid="referral-list-count" className="font-sans text-xs text-zinc-100/70">
            {studios(data.used)} {data.used === 1 ? 'has' : 'have'} used {admin ? "this client's" : 'your'} code
          </p>
          <p data-testid="referral-list-earned" className="font-sans text-xs font-medium text-zinc-100 mt-0.5">
            Earned so far: {dollars(data.earned)}
          </p>
        </div>
        {admin && (
          <div className="flex flex-wrap justify-end gap-1.5">{FILTERS.map(filterPill)}</div>
        )}
      </div>
      {rows.length === 0 ? (
        <p data-testid="referral-list-none-match" className="font-sans text-xs text-zinc-400">{filter.none}</p>
      ) : (
        <ul className="divide-y divide-ug-border-subtle border-t border-ug-border-subtle">
          {rows.map((row, i) => (
            <li key={`${row.name}-${i}`} data-testid="referral-list-row" className="py-2.5">
              <p className="font-sans text-[13px] text-zinc-100">{row.name}</p>
              <p data-testid="referral-list-status"
                className={`font-sans text-xs mt-0.5 ${row.pending?.delayed ? 'text-ug-status-red' : row.earned.length ? 'text-oxidized-teal' : 'text-zinc-400'}`}>
                {statusLine(row, data.reward_amounts, admin)}
              </p>
              {admin && row.last_order_on && (
                <p data-testid="referral-list-last-order" className="font-sans text-[11px] text-slate-500 mt-0.5">
                  Last paid order {adminDate(row.last_order_on)}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// The client's referral code on the drawer, and the team's hand on it: set a code an influencer
// will share, or draw a fresh random one, any number of times (PUT /referrals/{client}/code).
// The server answers in the same sentences the client's card uses.
function ReferralCodeControl({ clientId, code, chosen, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async (raw) => {
    setBusy(true); setNote('');
    try {
      const r = await api.put(`/referrals/${clientId}/code`, { code: raw });
      onChanged(r.data.code); setNote(r.data.message); setEditing(false); setValue('');
    } catch (e) {
      setNote(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail
        : 'We could not save that just now. Please try again in a minute.');
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="referral-code-control">
      <p className="text-[11px] text-zinc-100/70">
        Referral code <code data-testid="drawer-referral-code" className="font-mono text-[12px] tracking-[0.15em] text-zinc-100 ml-1">{code}</code>
        {chosen && <span className="ml-2 text-slate-500">chosen by the client</span>}
      </p>
      {editing ? (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <Input value={value} maxLength={12} autoFocus spellCheck={false}
            onChange={e => { setValue(e.target.value.toUpperCase()); setNote(''); }}
            onKeyDown={e => { if (e.key === 'Enter') save(value); }}
            placeholder="4 to 12 letters or numbers" data-testid="drawer-referral-code-input"
            className="h-8 w-48 font-mono text-xs tracking-[0.15em]" />
          <Button size="sm" onClick={() => save(value)} disabled={busy} data-testid="drawer-referral-code-save"
            className="tap-auto h-8 px-3 bg-ug-gold/15 text-ug-gold hover:bg-ug-gold/25 border border-ug-gold/40 font-sans text-[11px] font-medium">Save</Button>
          <button type="button" onClick={() => { setEditing(false); setValue(''); setNote(''); }} data-testid="drawer-referral-code-cancel"
            className="tap-auto text-[11px] text-slate-400 hover:text-zinc-100">Cancel</button>
        </div>
      ) : (
        <div className="flex items-center gap-3 mt-1.5">
          <button type="button" onClick={() => { setEditing(true); setNote(''); }} data-testid="drawer-referral-code-change"
            className="tap-auto text-[11px] text-ug-gold hover:underline">Set a code</button>
          <button type="button" onClick={() => save('')} disabled={busy} data-testid="drawer-referral-code-reset"
            className="tap-auto text-[11px] text-slate-400 hover:text-zinc-100 hover:underline">New random code</button>
        </div>
      )}
      {note && (
        <p data-testid="drawer-referral-code-note" className={`text-[11px] mt-1 ${note === 'That is your code now.' ? 'text-oxidized-teal' : 'text-ug-status-red'}`}>{note}</p>
      )}
    </div>
  );
}

// The drawer's block: this client's referral code with the team's control on it, and the studios
// they referred when there are any. A plain sentence when the list could not be read.
export function ReferredStudios({ clientId }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState('');
  useEffect(() => {
    setData(null); setFailed('');
    api.get(`/referrals/${clientId}/studios`).then(r => setData(r.data))
      .catch(e => setFailed(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail
        : 'We could not load this list just now. Please try again in a minute.'));
  }, [clientId]);
  if (!failed && !data) return null;
  return (
    <div className="mb-5" data-testid="drawer-referrals-made">
      <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-3">Referrals</h3>
      <div className="rounded-xl bg-ug-raised border border-ug-border p-4">
        {failed
          ? <p data-testid="referral-list-unavailable" className="font-sans text-xs text-zinc-400">{failed}</p>
          : (
            <>
              <ReferralCodeControl clientId={clientId} code={data.code} chosen={data.code_chosen}
                onChanged={code => setData(d => ({ ...d, code, code_chosen: false }))} />
              {data.used > 0 && (
                <div className="mt-3 pt-3 border-t border-ug-border-subtle"><ReferralList data={data} admin /></div>
              )}
            </>
          )}
      </div>
    </div>
  );
}
